import React from 'react';
import { Link } from 'react-router-dom';
import { usePublicLanguage } from '../contexts/PublicLanguageContext';
import './Landing.css';

// The iOS app. `ct` is an App Store Connect campaign token: App Analytics
// reports downloads and proceeds against it, which is the only way to tell
// how many installs the website actually produced -- there is no SDK in the
// app to report back. Add a `pt` provider token from App Store Connect ->
// App Analytics -> Acquisition -> Campaigns for full attribution.
const APP_STORE_URL = 'https://apps.apple.com/app/id6759010014?ct=web-landing';

// Text rather than Apple's badge artwork: the badge is trademarked and has to
// be their exact asset per Apple's identity guidelines, while the wording
// alone is theirs to specify and free to set in type. Swap in the official
// badge from Apple's marketing resources if you'd rather have it.
const AppStoreLink: React.FC<{ label: string }> = ({ label }) => (
  <a
    href={APP_STORE_URL}
    className="cta-button appstore"
    target="_blank"
    rel="noopener noreferrer"
  >
    {label}
  </a>
);

const Landing: React.FC = () => {
  const { t } = usePublicLanguage();

  return (
    <div className="landing-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-logo">
            <div className="logo-box">
              <img src="/logo192.png" alt="Yarnboard" className="brand-logo-img" />
            </div>
          </div>
          <h1 className="hero-title">{t.landing.title}</h1>
          <p className="hero-tagline">{t.landing.tagline}</p>
          <p className="hero-subtitle">{t.landing.subtitle}</p>
          <div className="hero-cta">
            <Link to="/login" className="cta-button primary">{t.landing.getStarted}</Link>
            <a href="#how-it-works" className="cta-button secondary">{t.landing.learnMore}</a>
            <AppStoreLink label={t.landing.appStore} />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="workflow-section" id="how-it-works">
        <div className="section-content">
          <h2 className="section-title">{t.landing.workflowTitle}</h2>
          <p className="section-subtitle">{t.landing.workflowSubtitle}</p>

          <div className="workflow-steps">
            {(['save', 'tag', 'find', 'connect', 'reuse'] as const).map((key, i, steps) => (
              <React.Fragment key={key}>
                <div className="workflow-step">
                  <div className="workflow-step-number">{t.landing.workflow[key].step}</div>
                  <h3>{t.landing.workflow[key].title}</h3>
                  <p>{t.landing.workflow[key].description}</p>
                </div>
                {i < steps.length - 1 && <div className="workflow-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="pain-section">
        <div className="section-content">
          <h2 className="section-title">{t.landing.painTitle}</h2>

          <div className="pain-grid">
            {(['screenshots', 'arguments', 'scattered', 'draft', 'connections', 'video', 'prompts'] as const).map(key => (
              <div className="pain-card" key={key}>
                <p className="pain-hook">{t.landing.pains[key].hook}</p>
                <p className="pain-answer">{t.landing.pains[key].answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-content">
          <h2 className="section-title">{t.landing.featuresTitle}</h2>
          <p className="section-subtitle">{t.landing.featuresSubtitle}</p>

          <div className="features-grid">
            {([
              { key: 'brain', icon: '🧠' },
              { key: 'web', icon: '🔍' },
              { key: 'recall', icon: '🎯' },
              { key: 'organization', icon: '🏷️' },
              { key: 'video', icon: '📹' },
              { key: 'documents', icon: '📄' },
              { key: 'versions', icon: '🕘' },
            ] as const).map(({ key, icon }) => (
              <div className="feature-card" key={key}>
                <div className="feature-icon">{icon}</div>
                <h3>
                  {t.landing.features[key].title}
                  {/* Not built yet — say so on the card rather than letting the
                      description read as a shipped feature */}
                  {'comingSoon' in t.landing.features[key]
                    && (t.landing.features[key] as { comingSoon?: boolean }).comingSoon && (
                    <span className="feature-badge">{t.landing.comingSoonBadge}</span>
                  )}
                </h3>
                <p>{t.landing.features[key].description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience Section */}
      <section className="audience-section">
        <div className="section-content">
          <h2 className="section-title">{t.landing.audienceTitle}</h2>

          <div className="audience-grid">
            {([
              { key: 'creators', icon: '🎥' },
              { key: 'journalists', icon: '📰' },
              { key: 'writers', icon: '✍️' },
              { key: 'researchers', icon: '🔬' },
              { key: 'social', icon: '📱' },
              { key: 'legal', icon: '⚖️' },
              { key: 'developers', icon: '💻' },
              { key: 'designers', icon: '🎨' },
            ] as const).map(({ key, icon }) => (
              <div className="audience-card" key={key}>
                <span className="audience-icon">{icon}</span>
                <h4>{t.landing.audiences[key].title}</h4>
                <p>{t.landing.audiences[key].description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>{t.landing.ctaTitle}</h2>
          <p>{t.landing.ctaSubtitle}</p>
          <div className="cta-buttons">
            <Link to="/login" className="cta-button primary large">{t.landing.ctaButton}</Link>
            <AppStoreLink label={t.landing.appStore} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
